import React, { useEffect, useState } from "react";

import { useGetRecentData, useSearchDatasets } from "api/api-hooks/datasets-hooks";
import { DatasetQueryParams } from "api/model/dataset/DatasetDataInfoResponse";
import ChartInDialogButton from "components/basic/chart-in-dialog-button/ChartInDialogButton";
import CopyTextWithButton from "components/basic/copy-text-with-button/CopyTextWithButton";
import {
  AutoComplete,
  AutoCompleteChangeEvent,
  AutoCompleteCompleteEvent,
} from "primereact/autocomplete";
import { T } from "services/lang/LanguageService";
import { timeAgo } from "utils/time-utils";
import { isDatasetIdentifier } from "utils/utils";
import "./style.scss";

interface IDatasetDropdownProps {
  value?: string | null;
  onChange: (e: string | null, label: string | null) => void;
  style?: React.CSSProperties;
  disabled?: boolean;
  deleted?: boolean;
  proxied?: boolean;
  hideButtons?: boolean;
}

interface DropdownItem {
  value: string | number;
  label: string;
}

// eslint-disable-next-line import/prefer-default-export
export function DatasetDropdown(props: IDatasetDropdownProps) {
  const [selectedDataset, setSelectedDataset] = useState<DropdownItem | null>(null);
  const [filteredDatasets, setFilteredDatasets] = useState<DropdownItem[]>([]);
  const [searchQueryParams, setSearchQueryParams] = useState<DatasetQueryParams>({
    query: "",
    limit: 100,
    identifiers: [],
    deleted: props.deleted,
    proxied: props.proxied,
  });

  const recentDataQuery = useGetRecentData([props.value!], { options: { enabled: !!props.value } });

  const { data: searchResults } = useSearchDatasets(searchQueryParams, {
    enabled: Number(searchQueryParams.query?.length) >= 3 || !!searchQueryParams.identifiers?.length,
  })

  useEffect(() => {
    if (recentDataQuery.data !== undefined && recentDataQuery.data.length > 0) {
      const dataset = recentDataQuery.data[0]!!;
      const label = `${dataset.title} `;

      if (dataset.data.length > 0) {
        const { key: time, value } = dataset.data[0];
        setSelectedDataset({
          value: dataset.identifier,
          label: `${label} | ${value.toFixed(2)} ${dataset.unit} | ${timeAgo(time)}`,
        });
      } else {
        setSelectedDataset({ value: dataset.identifier, label: `${label} | ${dataset.unit}` });
      }
    }
  }, [recentDataQuery.data]);

    useEffect(() => {
      if (searchResults) {
        const selections = searchResults.map(
          it =>
            ({
              value: it.identifier,
              label: it.toString(),
            }) as DropdownItem
        );
        setFilteredDatasets(selections);
      }
    }, [searchResults]);

  const searchDatasets = (e: AutoCompleteCompleteEvent) => {
    const searchStr = e.query.trim();

    if (searchStr.length < 3) return;

    const queryParams: DatasetQueryParams = {
      query: searchStr,
      limit: 100,
      identifiers: [],
      proxied: true,
    };
    
    if (isDatasetIdentifier(searchStr)) {
      queryParams.query = "";
      queryParams.identifiers = [searchStr];
      queryParams.limit = 20;
    }

    setSearchQueryParams(prev => ({ ...prev, ...queryParams }));
  };

  const onChange = (e: AutoCompleteChangeEvent) => {
    setSelectedDataset(e.value);
    if (typeof e.value === "string") return; // Ignore string auto complete dumb input

    props.onChange(e.value?.value || null, e.value?.label || null);
  };

  return (
    <div className="p-inputgroup flex w-full dataset-search">
      <AutoComplete
        placeholder={T("label.type-to-search-datasets")}
        value={selectedDataset}
        suggestions={filteredDatasets}
        onChange={onChange}
        completeMethod={searchDatasets}
        minLength={3}
        field="label"
        onClear={() => {
          setSelectedDataset(null);
          props.onChange(null, null);
        }}
        style={{ width: "100%", ...props.style }}
        disabled={props.disabled}
      />
      {props.value && !props.hideButtons && (
        <div className="p-inputgroup-addon">
          <CopyTextWithButton text="" copyableText={props.value} />
          <ChartInDialogButton identifier={props.value} header={T("word.dataset")} />
        </div>
      )}
    </div>
  );
}
